import { createContext, useContext } from 'react'

import type { Cue, Track } from './cues.js'

export interface SoundChoice {
  /** Play a cue, if this screen is one that makes a noise and sound is on. */
  readonly play: (cue: Cue) => void
  /** Ask for a loop, or `null` for none. Idempotent: asking twice is once. */
  readonly music: (track: Track | null) => void
  /** Whether sound is switched on — for a control that shows its state. */
  readonly on: boolean
  readonly setOn: (on: boolean) => void
  /**
   * Whether this screen makes any noise at all.
   *
   * The television does; nothing else does. A control for something that
   * cannot happen is worse than no control, so the switch reads this to say
   * so rather than sitting there pretending.
   */
  readonly heard: boolean
}

export const SoundContext = createContext<SoundChoice | null>(null)

/**
 * Sound, from anywhere inside the layout.
 *
 * Returns a working no-op outside a provider rather than throwing. A game
 * rendered on its own in a test should not have to know this exists, and a
 * missing provider silencing the app is a far better failure than a missing
 * provider crashing it in front of a child.
 */
export function useSound(): SoundChoice {
  return (
    useContext(SoundContext) ?? {
      play: () => undefined,
      music: () => undefined,
      on: false,
      setOn: () => undefined,
      heard: false,
    }
  )
}
